import { Injectable } from '@angular/core';

import { AnalyticsBase } from '@recruitee/analytics';
import type { OfferStatus } from '@recruitee/offers-types';

import { CloseEventProps, CopiedShareUrl, PublishEventProps } from './jobs.analytics.types';
import {
  OfferAnalytics,
  OffersGroupType,
  OffersColumnId,
  OffersScope,
  SocialMediaType,
} from './offer-analytics.types';

@Injectable()
export class JobsAnalytics extends AnalyticsBase implements OfferAnalytics {
  public readonly namespace: string = 'Jobs';
  public readonly requireViewContext: boolean = true;

  public trackPublish(props: PublishEventProps): void {
    this.track('Publish', props);
  }

  public trackClose(props: CloseEventProps): void {
    this.track('CloseJob', props);
  }

  public trackEdit(status: 'internal' | 'public' | 'draft' | 'closed' | 'archived'): void {
    this.track(
      'EditJob',
      { status },
      { integrations: { All: false, Mixpanel: true, Intercom: true } },
    );
  }

  public trackDuplicate(): void {
    this.track('Duplicate', {}, { integrations: { All: false, Mixpanel: true, Intercom: true } });
  }

  public trackPreviewJob(): void {
    this.track('ViewOffer');
  }

  public trackCopyJobLink(url: CopiedShareUrl): void {
    this.track('CopyURL', { url });
  }

  public trackAssignRequisitionToJob(hasSharedOpenings: boolean, jobStatus: OfferStatus) {
    this.track('AssignRequisitionToJob', { hasSharedOpenings, jobStatus });
  }

  public trackOpeningsFilled(count: number, isBasedOnRequisition: boolean) {
    this.track('OpeningsFilled', { count, isBasedOnRequisition });
  }

  public trackArchive(): void {
    this.track('Archive');
  }

  public trackDelete(): void {
    this.track('Delete');
  }

  public trackGroupBy(type: keyof typeof OffersGroupType): void {
    this.track('GroupBy', { type: OffersGroupType[type] });
  }

  public trackAdjustColumns(selectedField: keyof typeof OffersColumnId, enabled: boolean): void {
    this.track('AdjustColumns', { selectedField: OffersColumnId[selectedField], enabled });
  }

  public trackViewType(view: string): void {
    const type = view === 'list' ? 'Card' : 'Table';
    this.track('ChangeViewType', { type });
  }

  public trackChangeScope(scope: 'active' | 'archived'): void {
    this.track('ChangeFilterTab', {
      scope: OffersScope[scope],
      ...(OffersScope[scope] === OffersScope.active ? { type: 'All' } : {}),
    });
  }

  public trackChangeFilterTab(scope: keyof typeof OffersScope, tab: number): void {
    let tabName: string;

    switch (tab) {
      case 1: {
        tabName = 'Followed';
        break;
      }
      case 2: {
        tabName = 'Scheduled';
        break;
      }
      default: {
        tabName = 'All';
      }
    }

    this.track('ChangeFilterTab', { scope: OffersScope[scope], type: tabName });
  }

  public trackSaveNewUrl(): void {
    this.track('EditURL');
  }

  public trackShareOnSocialMedia(type: SocialMediaType): void {
    this.track('ShareOnSocialMedia', { type });
  }

  public trackCopyTrackableURL(source: string): void {
    this.track('CopyTrackableURL', { source });
  }
}
